import React, { useState, useEffect } from 'react';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress
} from '@mui/material';
import { Send, Telegram, ReportProblem, Refresh } from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import { driversApi, Driver } from '../services/driversApi';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';

interface ActiveAlert {
    id: string;
    type: string;
    target: string;
    message: string;
    driverId?: number;
    telegramChatId?: string;
}

const AlertMessages: React.FC = () => {
    const { t } = useLanguage();
    
    const [drivers, setDrivers] = useState<Driver[]>([]);
    const [alertsList, setAlertsList] = useState<ActiveAlert[]>([]);
    const [loading, setLoading] = useState(true);
    
    // Custom test alert form
    const [selectedDriverId, setSelectedDriverId] = useState<number | ''>('');
    const [customMessage, setCustomMessage] = useState('');

    const loadDriversAndGenerateAlerts = async () => {
        setLoading(true);
        try {
            const drvRes = await driversApi.getAll();
            if (drvRes.success) {
                const list: Driver[] = drvRes.data;
                setDrivers(list);

                // Auto-generate some alerts for testing
                const generatedAlerts: ActiveAlert[] = [];
                
                // 1. Check for drivers without telegram ID registered
                list.filter(d => !d.telegramChatId).forEach(d => {
                    generatedAlerts.push({
                        id: `no-tg-${d.id}`,
                        type: 'Configuration',
                        target: `${d.firstName} ${d.lastName}`,
                        message: "Pas d'identifiant de Chat Telegram configuré.",
                        driverId: d.id
                    });
                });

                // 2. Generate random mock notifications from documents / maintenance
                list.filter(d => d.telegramChatId).slice(0, 2).forEach((d, idx) => {
                    generatedAlerts.push({
                        id: `doc-exp-${d.id}`,
                        type: 'Visite Technique',
                        target: `${d.firstName} ${d.lastName}`,
                        message: "Visite technique du véhicule assigné expire dans 5 jours.",
                        driverId: d.id,
                        telegramChatId: d.telegramChatId
                    });
                });

                setAlertsList(generatedAlerts);
            }
        } catch (error) {
            console.error('Failed to load alert console:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadDriversAndGenerateAlerts();
    }, []);

    const getLocalizedAlertType = (type: string) => {
        if (type === 'Configuration') return t('parc.alerts.typeConfiguration') || 'Configuration';
        if (type === 'Visite Technique') return t('parc.lookups.docTypes') || 'Visite Technique';
        return type;
    };

    const getLocalizedAlertMessage = (msg: string) => {
        if (msg.includes("Pas d'identifiant de Chat Telegram")) {
            return t('parc.alerts.noTelegramChatId') || msg;
        }
        if (msg.includes("Visite technique du véhicule assigné")) {
            return t('parc.alerts.techControlExpiringSoon') || msg;
        }
        return msg;
    };

    const handleSendTelegram = async (alertItem: ActiveAlert) => {
        if (!alertItem.telegramChatId) {
            blobticMessage.error(t('parc.alerts.dispatchError') || "Erreur de dispatch", t('parc.alerts.noChatIdAssociated') || "Aucun Telegram Chat ID associé à ce conducteur.");
            return;
        }

        try {
            // Mock call representing bot notification dispatch
            blobticMessage.success(t('common.messages.success') || "Succès", t('parc.alerts.telegramSentSuccess', alertItem.target));
        } catch (err) {
            blobticMessage.error(t('parc.alerts.sendFailed') || "Échec de l'envoi", t('parc.alerts.telegramContactError') || "Impossible de contacter le serveur Telegram.");
        }
    };

    const handleSendCustomAlert = async () => {
        if (!selectedDriverId || !customMessage) {
            blobticMessage.error(t('common.fields.required') || "Champs requis", t('parc.alerts.selectDriverAndMessage') || "Veuillez sélectionner un conducteur et saisir un message.");
            return;
        }

        const driver = drivers.find(d => d.id === selectedDriverId);
        if (!driver) return;

        if (!driver.telegramChatId) {
            blobticMessage.error(t('parc.alerts.noChatId') || "Pas de Chat ID", t('parc.alerts.driverNoTelegram') || "Ce conducteur ne possède pas de compte Telegram enregistré.");
            return;
        }

        try {
            blobticMessage.success(t('common.messages.success') || "Succès", t('parc.alerts.customMessageSentSuccess', `${driver.firstName} ${driver.lastName}`));
            setCustomMessage('');
        } catch (err) {
            blobticMessage.error(t('common.messages.error') || "Erreur", t('parc.alerts.sendErrorOccurred') || "Une erreur s'est produite lors de l'envoi.");
        }
    };

    if (loading) {
        return (
            <GenericPage title={t('parc.alerts.consoleTitle') || "Console d'Alerte et Messages Bot"} footerEnabled={false}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                    <CircularProgress size={50} />
                </Box>
            </GenericPage>
        );
    }

    return (
        <GenericPage title={t('parc.alerts.consoleTitle') || "Console d'Alerte et Messages Bot"} footerEnabled={false}>
            <Grid container spacing={3}>
                
                {/* Info Telegram bot banner */}
                <Grid item xs={12}>
                    <Alert severity="info" variant="outlined" icon={<Telegram />}>
                        {t('parc.alerts.telegramConfigInfo') || "Pour recevoir des notifications automatiques, configurez le Chat ID Telegram dans le profil de chaque conducteur. Les conducteurs doivent d'abord lancer le bot de la flotte."}
                    </Alert>
                </Grid>

                {/* Active warnings and quick dispatch panel */}
                <Grid item xs={12} md={7}>
                    <Card sx={{ minHeight: 400 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">{t('parc.alerts.activeFleetAlerts') || 'Alertes Flotte Actives'}</Typography>
                                <Button startIcon={<Refresh />} onClick={loadDriversAndGenerateAlerts} size="small">
                                    {t('btn.refresh') || 'Actualiser'}
                                </Button>
                            </Box>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><b>{t('parc.gpsalerts.type') || 'Type'}</b></TableCell>
                                            <TableCell><b>{t('parc.alerts.target') || 'Cible'}</b></TableCell>
                                            <TableCell><b>{t('parc.gpsalerts.message') || 'Message'}</b></TableCell>
                                            <TableCell align="right"><b>{t('parc.gpsalerts.action') || 'Action'}</b></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {alertsList.map((alert) => (
                                            <TableRow key={alert.id}>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <ReportProblem color="warning" fontSize="small" />
                                                        <Typography variant="body2">{getLocalizedAlertType(alert.type)}</Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{alert.target}</TableCell>
                                                <TableCell>{getLocalizedAlertMessage(alert.message)}</TableCell>
                                                <TableCell align="right">
                                                    <Button 
                                                        size="small" 
                                                        variant="contained" 
                                                        color="secondary"
                                                        startIcon={<Telegram />}
                                                        disabled={!alert.telegramChatId}
                                                        onClick={() => handleSendTelegram(alert)}
                                                    >
                                                        {t('parc.alerts.notify') || 'Notifier'}
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {alertsList.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center">{t('parc.alerts.noAlertsPending') || 'Aucune alerte en attente'}</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Custom dispatcher tool */}
                <Grid item xs={12} md={5}>
                    <Card sx={{ minHeight: 400 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>{t('parc.alerts.sendManualMessage') || 'Envoyer un Message Manuel'}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                {t('parc.alerts.sendManualMessageDesc') || 'Envoyez une directive instantanée à un conducteur sur son canal Telegram personnel.'}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <FormControl fullWidth required>
                                    <InputLabel>{t('parc.alerts.recipientDriver') || 'Conducteur destinataire'}</InputLabel>
                                    <Select
                                        value={selectedDriverId}
                                        label={t('parc.alerts.recipientDriver') || 'Conducteur destinataire'}
                                        onChange={(e) => setSelectedDriverId(e.target.value as number)}
                                    >
                                        {drivers.map((d) => (
                                            <MenuItem key={d.id} value={d.id}>
                                                {d.firstName} {d.lastName} {d.telegramChatId ? `(TG: ${d.telegramChatId})` : `(${t('parc.alerts.noTelegram') || 'Aucun Telegram'})`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    label={t('parc.gpsalerts.message') || 'Message'}
                                    placeholder={t('parc.alerts.messagePlaceholder') || "Entrez vos consignes ici..."}
                                    value={customMessage}
                                    onChange={(e) => setCustomMessage(e.target.value)}
                                    required
                                />

                                <Button
                                    variant="contained"
                                    color="primary"
                                    endIcon={<Send />}
                                    onClick={handleSendCustomAlert}
                                >
                                    {t('parc.alerts.sendMessage') || 'Envoyer le message'}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

            </Grid>
        </GenericPage>
    );
};

export default AlertMessages;

